'use client'
import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const getVideoEmbed = (src) => {
  // YouTube embed URL (already embedded)
  if (src.includes("youtube.com/embed/")) {
    return { type: "iframe", src };
  }

  // YouTube watch URL: https://www.youtube.com/watch?v=ID
  const watchMatch = src.match(/youtube\.com\/watch\?v=([\w-]+)/);
  if (watchMatch) {
    return { type: "iframe", src: `https://www.youtube.com/embed/${watchMatch[1]}` };
  }

  // YouTube short URL: https://youtu.be/ID
  const shortMatch = src.match(/youtu\.be\/([\w-]+)/);
  if (shortMatch) {
    return { type: "iframe", src: `https://www.youtube.com/embed/${shortMatch[1]}` };
  }

  // Vimeo URL: https://vimeo.com/ID
  const vimeoMatch = src.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { type: "iframe", src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }

  // Direct video file (.mp4, .webm, .ogg, etc.)
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(src)) {
    return { type: "video", src };
  }

  // Fallback: try rendering as iframe
  return { type: "iframe", src };
};

const Modal = ({ mediaSrc, mediaType, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  const renderMedia = () => {
    if (mediaType === "image") {
      return (
        <img
          src={mediaSrc}
          alt="Content"
          className="w-full h-auto object-cover rounded"
        />
      );
    }

    const { type, src } = getVideoEmbed(mediaSrc);

    if (type === "iframe") {
      return (
        <iframe
          src={src}
          className="w-full aspect-video rounded"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    return (
      <video controls className="w-full h-auto rounded">
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <div className="fixed inset-0 bg-[#00000080] flex justify-center items-center z-999">
      <motion.div
        ref={modalRef}
        className=" rounded-lg p-1 w-3/4 sm:w-1/2 md:w-2/4"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          {renderMedia()}
          <button
            onClick={onClose}
            className="absolute top-1 md:top-2 right-2 text-white text-2xl bg-gray-700 p-2 
            rounded-full hover:bg-gray-600 transition cursor-pointer w-8 h-8 md:w-12 md:h-12 flex items-center justify-center"
          >
            &times;
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Modal;